import useUserInfo from '../../../hooks/useUserInfo';
import { useSelector } from 'react-redux';
import { selectUserInfoData } from '../../../reducers/userInfoReducer';
import { AccountType } from '@server/account-type';
import { DateTime } from 'luxon';
import usePermission from '../../common/permissions/usePermission';
import { openIntercom, scheduleCall } from '../customerSupportHelper';
import { useCallback, useMemo } from 'react';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { PAYWALL_EVENTS } from '../../../services/events';
import { ACTIVATE_TRIAL_COOKIE_NAME } from '../paywall/paywallConsts';
import { useFeatureToggle } from '../../common/useFeatureToggle';
import { PaywallSource, PaywallBlockProps, PaywallState } from '../paywall/paywallTypes';

export enum PaywallAction {
  UPGRADE = 'UPGRADE', //NOT relevant for the "sales activated trial" feature
  START_TRIAL = 'START_TRIAL', //NOT relevant for the "sales activated trial" feature
  NONE = 'NONE',
  TALK_TO_SALES = 'TALK_TO_SALES', //relevant ONLY for the "sales activated trial" feature
}

const INTERCOM_MESSAGE = `Hi! I'm interested in learning more about upgrade options`;

const ACTIVE_PAYWALL_STATES = [PaywallState.TRIAL_EXPIRED, PaywallState.PRE_TRIAL];
const ONLY_UPGRADE_STATES = [PaywallState.TRIAL_EXPIRED, PaywallState.IN_TRIAL];

const getPaywallState = (type?: AccountType, trialEndDate?: string) => {
  switch (type) {
    case AccountType.Customer:
    case AccountType.Internal:
    case AccountType.POC:
      return PaywallState.CUSTOMER;
    case AccountType.Inactive:
      return PaywallState.TRIAL_EXPIRED;
    case AccountType.Free:
      if (!trialEndDate) {
        return PaywallState.PRE_TRIAL;
      }
      const trialEnd = DateTime.fromISO(trialEndDate);
      const now = DateTime.fromISO(new Date().toISOString());
      if (trialEnd >= now) {
        return PaywallState.IN_TRIAL;
      } else {
        return PaywallState.TRIAL_EXPIRED;
      }
    default:
      return PaywallState.TRIAL_EXPIRED;
  }
};

const calcDaysLeft = (trialEndDate: string) => {
  const trialEnd = DateTime.fromISO(trialEndDate);
  const now = DateTime.now();
  const diff = trialEnd.diff(now, ['days']).toObject();
  return Math.ceil(diff?.days ?? 0);
};

const getPaywallAction = ({
  isPaywallActive,
  salesActivatedTrial,
  isOnlyUpgradeAvailable,
  isAllowedStartTrial,
}: {
  isPaywallActive: boolean;
  salesActivatedTrial: boolean;
  isOnlyUpgradeAvailable: boolean;
  isAllowedStartTrial: boolean;
}) => {
  if (!isPaywallActive) {
    return PaywallAction.NONE;
  }
  if (salesActivatedTrial) {
    return PaywallAction.TALK_TO_SALES;
  }
  if (isOnlyUpgradeAvailable || !isAllowedStartTrial) {
    return PaywallAction.UPGRADE;
  }
  return PaywallAction.START_TRIAL;
};

const usePaywall = () => {
  const { salesActivatedTrial } = useFeatureToggle();
  const { startTrial } = useUserInfo();
  const user = useSelector(selectUserInfoData);
  const [isAllowedStartTrial] = usePermission(['create:users:account:trial']);
  const { trialEndDate, type } = user?.account ?? {};
  const openIntercomForUpgrade = useCallback(() => openIntercom(INTERCOM_MESSAGE), []);
  const sendBiEvent = useSendBiEvent();

  const paywallState = useMemo(() => {
    return getPaywallState(type, trialEndDate);
  }, [trialEndDate, type]);

  const daysLeft = useMemo(() => {
    return paywallState === PaywallState.IN_TRIAL && trialEndDate
      ? calcDaysLeft(trialEndDate)
      : null;
  }, [paywallState, trialEndDate]);

  const isPaywallActive = useMemo(
    () => ACTIVE_PAYWALL_STATES.includes(paywallState),
    [paywallState],
  );
  const isOnlyUpgradeAvailable = useMemo(
    () => ONLY_UPGRADE_STATES.includes(paywallState),
    [paywallState],
  );

  const paywallAction = useMemo(() => {
    return getPaywallAction({
      isPaywallActive,
      salesActivatedTrial,
      isOnlyUpgradeAvailable,
      isAllowedStartTrial,
    });
  }, [isAllowedStartTrial, isOnlyUpgradeAvailable, isPaywallActive, salesActivatedTrial]);

  const sendTrialClickMainEvent = useCallback(() => {
    sendBiEvent({
      name: PAYWALL_EVENTS.clickTrialMain,
    });
  }, [sendBiEvent]);

  const sendTrialActivateEvent = useCallback(
    (source: PaywallSource | string) => {
      sendBiEvent({
        name: PAYWALL_EVENTS.trialActivated,
        props: {
          source,
        },
      });
    },
    [sendBiEvent],
  );

  const removeTrialCookie = useCallback(() => {
    document.cookie =
      ACTIVATE_TRIAL_COOKIE_NAME + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }, []);

  const sendEncounterPaywallEvent = useCallback(
    (paywallBlockProps: PaywallBlockProps) => {
      const { source, objectName } = paywallBlockProps;
      sendBiEvent({
        name: PAYWALL_EVENTS.paywall,
        props: {
          trialStatus: paywallState,
          objectName,
          source,
        },
      });
    },
    [paywallState, sendBiEvent],
  );

  const sendEncounterPaywallPageEvent = useCallback(
    (paywallBlockProps: PaywallBlockProps) => {
      const { source } = paywallBlockProps;
      sendBiEvent({
        name: PAYWALL_EVENTS.paywall,
        props: {
          trialStatus: paywallState,
          source,
        },
      });
    },
    [paywallState, sendBiEvent],
  );

  const startTrialWithEvent = useCallback(
    async (source: PaywallSource) => {
      await startTrial();
      sendTrialActivateEvent(source);
      removeTrialCookie();
    },
    [startTrial, sendTrialActivateEvent, removeTrialCookie],
  );

  const ctaCallback = useCallback(
    (source?: PaywallSource) => {
      scheduleCall(user);

      sendBiEvent({
        name: PAYWALL_EVENTS.ctaClicked,
        props: {
          trialStatus: paywallState,
          source,
        },
      });
    },
    [user, sendBiEvent, paywallState],
  );

  const ctaClickedCallback = useCallback(
    (source?: PaywallSource) => {
      sendBiEvent({
        name: PAYWALL_EVENTS.ctaClicked,
        props: {
          trialStatus: paywallState,
          source,
        },
      });
    },
    [sendBiEvent, paywallState],
  );

  const connectToSfCallback = useCallback(
    (source?: PaywallSource) => {
      sendBiEvent({
        name: PAYWALL_EVENTS.connectToSfClick,
        props: {
          trialStatus: paywallState,
          source,
        },
      });
    },
    [sendBiEvent, paywallState],
  );

  const scheduleMeetingCallback = useCallback(
    (source?: PaywallSource) => {
      sendBiEvent({
        name: PAYWALL_EVENTS.scheduleMeetingClick,
        props: {
          trialStatus: paywallState,
          source,
        },
      });
    },
    [sendBiEvent, paywallState],
  );

  const chatWithUsCallback = useCallback(
    (source?: PaywallSource) => {
      sendBiEvent({
        name: PAYWALL_EVENTS.chatWithUsClick,
        props: {
          trialStatus: paywallState,
          source,
        },
      });
    },
    [sendBiEvent, paywallState],
  );

  return {
    startTrial: startTrialWithEvent,
    daysLeft,
    isPaywallActive,
    paywallAction, //TODO can be deleted SWEEP-5282
    openIntercomForUpgrade, //TODO can be deleted SWEEP-5282
    sendTrialClickMainEvent, //TODO can be deleted SWEEP-5282
    sendEncounterPaywallEvent,
    ctaCallback,
    ctaClickedCallback,
    connectToSfCallback,
    chatWithUsCallback,
    scheduleMeetingCallback,
    sendEncounterPaywallPageEvent,
    paywallState,
  };
};

export default usePaywall;
